"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Image from "next/image";
import {
  FaStar,
  FaGoogle,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { trackPostHogEvent } from "@/lib/posthog/client";

interface Review {
  author_name: string;
  rating: number;
  relative_time_description: string;
  text: string;
  profile_photo_url?: string;
}

const fallbackReviews: Review[] = [
  {
    author_name: "Corelle Chester",
    rating: 5,
    relative_time_description: "2 months ago",
    text: "Our service experience with Ruff Roofing was nothing short of exceptional. Our sales representative Mustafa Yousif was very knowledgeable, professional, and attentive at all times. We received a complete roof replacement. Everything went exactly as quoted. I would highly recommend Ruff Roofing.",
  },
  {
    author_name: "Robert Gray",
    rating: 5,
    relative_time_description: "3 months ago",
    text: "The crew arrived on the date expected and got right to work, pointed out some flaws with the under layment and finished up the same day. I have no complaints and am happy with the work they did.",
  },
  {
    author_name: "Karina Martinez",
    rating: 5,
    relative_time_description: "4 months ago",
    text: "Thanks to Kevin, who was super kind and patient. He explained the entire process and what the project would be like very well. Thanks for all your help. Excellent service.",
  },
  {
    author_name: "Luis Romeu",
    rating: 5,
    relative_time_description: "3 months ago",
    text: "Excellent Service, Work done on time, nice looking and professional services.",
  },
  {
    author_name: "Miguel Sandoval",
    rating: 5,
    relative_time_description: "5 months ago",
    text: "Excellent work by Cesar and his team. Professional, on time, and great quality roofing work.",
  },
];

const AVATAR_COLORS = ["#4285F4", "#DB4437", "#F4B400", "#0F9D58"];

function ReviewCard({ review }: { review: Review }) {
  const initial = review.author_name.charAt(0).toUpperCase();
  const bgColor = AVATAR_COLORS[review.author_name.length % AVATAR_COLORS.length];

  return (
    <div className="snap-start shrink-0 w-[85vw] sm:w-[340px] lg:w-[360px] bg-[#F9FAFB] rounded-2xl p-5 sm:p-6 flex flex-col min-h-[240px] sm:min-h-[260px] border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all duration-300">
      {/* Stars + Google badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <FaStar
              key={i}
              className={`text-sm ${i <= review.rating ? "text-yellow-400" : "text-gray-200"}`}
            />
          ))}
        </div>
        <FaGoogle className="text-[#4285F4] opacity-50" />
      </div>

      {/* Review text */}
      <p className="text-[#1C1C1C]/75 text-sm leading-relaxed flex-1 line-clamp-5">
        &ldquo;{review.text}&rdquo;
      </p>

      {/* Author */}
      <div className="mt-4 pt-4 border-t border-gray-200/60 flex items-center gap-3">
        {review.profile_photo_url ? (
          <Image
            src={review.profile_photo_url}
            alt={review.author_name}
            width={36}
            height={36}
            className="w-9 h-9 rounded-full object-cover shrink-0"
            unoptimized
          />
        ) : (
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0"
            style={{ backgroundColor: bgColor }}
          >
            {initial}
          </div>
        )}
        <div className="min-w-0">
          <p className="text-[#1C1C1C] font-semibold text-sm truncate">
            {review.author_name}
          </p>
          <p className="text-[#54595F] text-xs">
            {review.relative_time_description}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ReviewsAndSocial({
  compactTop = false,
  title,
  variant = "default",
  summary,
}: {
  compactTop?: boolean;
  /** Overrides the default "What Our Clients Say." heading. */
  title?: string;
  /** "summary" adds a rating panel beside the carousel (homepage). */
  variant?: "default" | "summary";
  /** Fixed figures for a city office; omit rating to show the count only. */
  summary?: { name: string; rating?: number; total: number };
} = {}) {
  const [reviews, setReviews] = useState<Review[]>(fallbackReviews);
  const [stats, setStats] = useState({ rating: 4.8, total: 468 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetch("/api/reviews")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.reviews?.length) {
          const withText = data.reviews.filter((r: Review) => r.text);
          if (withText.length > 0) setReviews(withText);
        }
        if (data?.rating && data?.total) {
          setStats({ rating: data.rating, total: data.total });
        }
      })
      .catch(() => {});
  }, []);

  const displayReviews = reviews.filter((r) => r.text);

  const updateScrollState = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollState, { passive: true });
    updateScrollState();
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  const getCardWidth = useCallback(() => {
    if (!scrollRef.current) return 380;
    const firstCard = scrollRef.current.firstElementChild as HTMLElement;
    return firstCard ? firstCard.offsetWidth + 20 : 380;
  }, []);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    trackPostHogEvent("reviews_carousel_scrolled", {
      direction: dir,
    });
    const w = getCardWidth();
    scrollRef.current.scrollBy({
      left: dir === "left" ? -w : w,
      behavior: "smooth",
    });
  };

  // Auto-scroll
  useEffect(() => {
    if (isPaused || displayReviews.length <= 2) return;
    const interval = setInterval(() => {
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const w = scrollRef.current.firstElementChild
        ? (scrollRef.current.firstElementChild as HTMLElement).offsetWidth + 20
        : 380;
      if (scrollLeft + clientWidth >= scrollWidth - 10) {
        scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        scrollRef.current.scrollBy({ left: w, behavior: "smooth" });
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPaused, displayReviews.length]);

  if (variant === "summary") {
    return (
      <section className="bg-white px-4 py-12 md:py-16">
        <div className="mx-auto max-w-7xl">
          <h2 className="mx-auto max-w-4xl text-center font-['Outfit'] text-3xl font-extrabold leading-tight text-[#1C1C1C] sm:text-4xl md:text-5xl">
            {title ?? "What Our Clients Say."}
          </h2>

          <div className="mt-10 grid gap-6 lg:grid-cols-[260px_1fr] lg:items-center">
            {/* Rating summary */}
            <div className="flex flex-col items-center gap-2 rounded-2xl bg-[#F3F3F3] p-6 text-center ring-1 ring-black/5 lg:items-start lg:bg-transparent lg:p-0 lg:text-left lg:ring-0">
              <p className="font-['Outfit'] text-lg font-extrabold uppercase tracking-wide text-[#1C1C1C]">
                {summary?.name ?? "Ruff Roofing"}
              </p>
              {(summary ? summary.rating !== undefined : true) && (
                <div className="flex items-center gap-2">
                  <span className="font-['Outfit'] text-2xl font-extrabold text-[#1C1C1C]">
                    {(summary?.rating ?? stats.rating).toFixed(1)}
                  </span>
                  <div className="flex gap-0.5 text-yellow-400">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <FaStar key={i} />
                    ))}
                  </div>
                </div>
              )}
              <p className="text-sm text-[#54595F]">
                {summary && summary.rating === undefined
                  ? `${summary.total} Google reviews`
                  : `Based on ${summary?.total ?? stats.total} Google reviews`}
              </p>
              <a
                href="https://maps.app.goo.gl/M2yao4sV4cAsfAL2A"
                target="_blank"
                rel="noopener noreferrer"
                data-ph-event="review_us_clicked"
                data-ph-label="Review Us on Google"
                data-ph-location="homepage_reviews_summary"
                className="mt-2 inline-flex min-h-[44px] items-center gap-2 rounded-full bg-[#DC3332] px-6 text-sm font-bold text-white transition-colors hover:bg-red-700"
              >
                <FaGoogle />
                Review Us on Google
              </a>
            </div>

            {/* Carousel */}
            <div
              className="relative min-w-0"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              <div
                ref={scrollRef}
                className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar pb-2"
              >
                {displayReviews.map((review, i) => (
                  <ReviewCard key={i} review={review} />
                ))}
              </div>
              <div className="mt-5 flex justify-center gap-3 lg:justify-end">
                <button
                  onClick={() => scroll("left")}
                  disabled={!canScrollLeft}
                  data-ph-event="reviews_carousel_arrow_clicked"
                  data-ph-label="left"
                  data-ph-location="homepage_reviews_summary"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-all hover:border-gray-400 hover:text-[#1C1C1C] disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Previous reviews"
                >
                  <FaChevronLeft className="text-xs" />
                </button>
                <button
                  onClick={() => scroll("right")}
                  disabled={!canScrollRight}
                  data-ph-event="reviews_carousel_arrow_clicked"
                  data-ph-label="right"
                  data-ph-location="homepage_reviews_summary"
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-all hover:border-gray-400 hover:text-[#1C1C1C] disabled:cursor-not-allowed disabled:opacity-30"
                  aria-label="Next reviews"
                >
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={`bg-white pb-6 md:pb-8 px-4 ${
        compactTop ? "pt-4 md:pt-6" : "pt-8 md:pt-12"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 sm:mb-12">
          <h2 className="font-['Outfit'] text-[32px] sm:text-5xl md:text-7xl lg:text-[90px] font-extrabold text-[#DC3332] leading-[0.95] text-left whitespace-nowrap">
            What Our Clients Say.
          </h2>
          <p className="mt-0.5 text-[#54595F] font-semibold text-[11px] sm:text-base md:text-lg lg:text-xl text-right whitespace-nowrap">
            Verified Google reviews from homeowners across Texas.
          </p>
        </div>

        {/* Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory no-scrollbar pb-2"
          >
            {displayReviews.map((review, i) => (
              <ReviewCard key={i} review={review} />
            ))}
          </div>
        </div>

        {/* Bottom: Arrows + CTA */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-between mt-6 sm:mt-8 gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => scroll("left")}
              disabled={!canScrollLeft}
              data-ph-event="reviews_carousel_arrow_clicked"
              data-ph-label="left"
              data-ph-location="reviews_section"
              className="w-11 h-11 sm:w-10 sm:h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#1C1C1C] hover:border-gray-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous reviews"
            >
              <FaChevronLeft className="text-xs" />
            </button>
            <button
              onClick={() => scroll("right")}
              disabled={!canScrollRight}
              data-ph-event="reviews_carousel_arrow_clicked"
              data-ph-label="right"
              data-ph-location="reviews_section"
              className="w-11 h-11 sm:w-10 sm:h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-[#1C1C1C] hover:border-gray-400 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Next reviews"
            >
              <FaChevronRight className="text-xs" />
            </button>
          </div>

          <a
            href="https://maps.app.goo.gl/M2yao4sV4cAsfAL2A"
            target="_blank"
            rel="noopener noreferrer"
            data-ph-event="review_us_clicked"
            data-ph-label="Review Us on Google"
            data-ph-location="reviews_section"
            className="inline-flex items-center gap-2 bg-[#1C1C1C] text-white rounded-full px-6 py-3.5 sm:py-3 text-sm font-semibold hover:bg-[#333] transition-colors w-full sm:w-auto justify-center min-h-[48px]"
          >
            <FaGoogle className="text-base" />
            Review Us on Google
          </a>
        </div>
      </div>
    </section>
  );
}
